// src/utils/cache/minimal-spots-cache.js
// Unified cache for minimal spot data (id, lat, lng, icon, color, tags)
// Single source of truth for all marker-level data across Explore and Discover

import { DATA_PATHS } from '../../dataPaths.js';
import { LOGGING } from '/settings.js';
import { fetchWithCache } from './data-cache.js';
import { getTagsForPack } from './explore-tags-cache.js';

/**
 * Minimal Spots Cache - In-memory cache by area
 * Maps areaId -> Map<spotId, minimalData>
 */
const minimalSpotsCache = new Map();

/**
 * In-flight requests - tracks ongoing area-wide fetches
 * Maps areaId -> Promise
 */
const inFlightAreaRequests = new Map();

/**
 * Preload all minimal spots for an entire area.
 * This is the primary data source for both Explore and Discover.
 * @param {number|string} areaId - Area ID
 * @returns {Promise<Map<number, Object>>} Map of spotId -> minimal data
 */
export async function preloadMinimalForArea(areaId) {
    if (!areaId) return new Map();
    const aid = Number(areaId);

    // Check if we already have the area cached
    if (minimalSpotsCache.has(aid)) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Minimal Spots Cache] HIT for area: ${aid}`);
        }
        return minimalSpotsCache.get(aid);
    }

    // Check if a request for this area is already in flight
    if (inFlightAreaRequests.has(aid)) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Minimal Spots Cache] Waiting for in-flight area fetch: ${aid}`);
        }
        return await inFlightAreaRequests.get(aid);
    }

    // Start new area-wide fetch
    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log(`[Minimal Spots Cache] Fetching all minimal spots for area: ${aid}`);
    }

    const fetchPromise = (async () => {
        try {
            const { getCurrentLanguage } = await import('../../translations/translations.js');
            const params = new URLSearchParams();
            params.append('area_id', aid);
            params.append('lang', getCurrentLanguage());

            const url = `/app_core/api/sqlite/get-spots-for-area.php?${params.toString()}`;
            const data = await fetchWithCache(url);

            const areaMap = new Map();
            if (data && Array.isArray(data.minimal)) {
                data.minimal.forEach(spot => {
                    const sid = Number(spot.id);
                    areaMap.set(sid, {
                        id: sid,
                        latitude: parseFloat(spot.latitude),
                        longitude: parseFloat(spot.longitude),
                        icon: spot.icon,
                        color: spot.color,
                        tags: Array.isArray(spot.tags) ? spot.tags.map(Number) : [],
                        coordinates: spot.coordinates || {
                            latitude: parseFloat(spot.latitude),
                            longitude: parseFloat(spot.longitude)
                        }
                    });
                });
            }

            minimalSpotsCache.set(aid, areaMap);
            return areaMap;
        } catch (error) {
            console.error(`[Minimal Spots Cache] Error loading area ${aid}:`, error);
            const emptyMap = new Map();
            minimalSpotsCache.set(aid, emptyMap);
            return emptyMap;
        } finally {
            inFlightAreaRequests.delete(aid);
        }
    })();

    inFlightAreaRequests.set(aid, fetchPromise);
    return await fetchPromise;
}

/**
 * Get minimal results for a pack (Explore view).
 * Filters the area-wide cache by the tags contained in the pack.
 * @param {number|string} areaId - Area ID
 * @param {number|string} packId - Pack ID
 * @param {Array<number>} [tagIds] - Specific tag IDs to filter by (optional)
 * @returns {Promise<{ids: Array<number>, minimal: Array}>} Filtered results
 */
export async function getMinimalResultsForPack(areaId, packId, tagIds = []) {
    const aid = Number(areaId);
    const pid = Number(packId);

    // Ensure area data is preloaded
    const areaMap = await preloadMinimalForArea(aid);

    // Get tags for this pack (from cache)
    const packTags = await getTagsForPack(aid, pid);
    const packTagIds = Array.isArray(packTags) ? packTags.map(t => Number(t.id)) : [];

    // Determine which tag IDs to filter by
    // If specific tagIds provided, use intersection. If none, use all pack tags.
    const filterTagIds = (tagIds && tagIds.length > 0)
        ? tagIds.map(Number).filter(id => packTagIds.includes(id))
        : packTagIds;

    const filtered = Array.from(areaMap.values()).filter(spot => {
        if (!spot.tags || spot.tags.length === 0) return false;
        return spot.tags.some(tid => filterTagIds.includes(tid));
    });

    return {
        ids: filtered.map(s => s.id),
        minimal: filtered
    };
}

/**
 * Get minimal results for specific spot IDs (Discover/Maps view).
 * @param {number|string} areaId - Area ID
 * @param {Array<number|string>} spotIds - Array of spot IDs
 * @returns {Promise<Array>} Array of minimal spot objects
 */
export async function getMinimalResultsForSpots(areaId, spotIds) {
    if (!spotIds || !Array.isArray(spotIds) || spotIds.length === 0) return [];

    const aid = Number(areaId);
    const areaMap = await preloadMinimalForArea(aid);

    return spotIds
        .map(id => areaMap.get(Number(id)))
        .filter(Boolean);
}

/**
 * Synchronous version of getMinimalResultsForSpots.
 * Returns whatever is currently in memory.
 */
export function getCachedMinimalResults(areaId, packId = null) {
    const aid = Number(areaId);
    const areaMap = minimalSpotsCache.get(aid);
    if (!areaMap) return null;

    if (packId !== null) {
        // This is a bit of a legacy shape for Explore
        // In a real sync call, we might not have the pack-to-tag mapping ready
        // so we return the whole area map and let caller filter if they can.
        // But for Explore, usually we want the pack-specific list.
        return null; // Force async if they want pack-specific sync and it's not ready
    }

    return areaMap;
}

/**
 * Clear the cache for a specific area or all areas.
 */
export function clearMinimalSpotsCache(areaId = null) {
    if (areaId !== null) {
        minimalSpotsCache.delete(Number(areaId));
    } else {
        minimalSpotsCache.clear();
    }
}

/**
 * For compatibility: populate cache from external data
 */
export function populateMinimalResults(areaId, minimalData) {
    if (!areaId || !Array.isArray(minimalData)) return;
    const aid = Number(areaId);

    let areaMap = minimalSpotsCache.get(aid);
    if (!areaMap) {
        areaMap = new Map();
        minimalSpotsCache.set(aid, areaMap);
    }

    minimalData.forEach(spot => {
        const sid = Number(spot.id);
        areaMap.set(sid, {
            id: sid,
            latitude: parseFloat(spot.latitude),
            longitude: parseFloat(spot.longitude),
            icon: spot.icon,
            color: spot.color,
            tags: Array.isArray(spot.tags) ? spot.tags.map(Number) : [],
            coordinates: spot.coordinates || {
                latitude: parseFloat(spot.latitude),
                longitude: parseFloat(spot.longitude)
            }
        });
    });
}
